import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const stockNumber = searchParams.get('stock') || '';
  const vehicleName = searchParams.get('vehicle') || '';

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (stockNumber && vehicleName) {
      setForm((prev) => ({
        ...prev,
        subject: `Inquiry about ${vehicleName} (Stock: ${stockNumber})`,
        message: `Hello Partners4Cars,\n\nI am interested in the ${vehicleName} (Stock #: ${stockNumber}). Please provide me with more information.\n\nThank you.`
      }));
    }
  }, [stockNumber, vehicleName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError(t('contact.error'));
    }
    setSending(false);
  };

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
            Partners4Cars
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white font-[Chivo]">
            {t('contact.title')}
          </h1>
          <p className="text-base md:text-lg text-zinc-500 mt-4 max-w-2xl">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#121212] border border-zinc-800/50 rounded-md p-12 text-center"
                data-testid="contact-success"
              >
                <CheckCircle className="w-12 h-12 text-[#25D366] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-white text-lg">{t('contact.success')}</p>
                <Button
                  onClick={() => setSent(false)}
                  variant="ghost"
                  className="mt-6 text-zinc-400 hover:text-white text-sm uppercase tracking-widest"
                >
                  Send Another
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('contact.name')}</Label>
                    <Input
                      required
                      value={form.name}
                      onChange={update('name')}
                      className="bg-transparent border-b border-white/10 rounded-none px-0 py-4 text-white placeholder:text-zinc-700 focus:border-[#ED2227] transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('contact.email')}</Label>
                    <Input
                      required
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      className="bg-transparent border-b border-white/10 rounded-none px-0 py-4 text-white placeholder:text-zinc-700 focus:border-[#ED2227] transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('contact.phone')}</Label>
                    <Input
                      value={form.phone}
                      onChange={update('phone')}
                      className="bg-transparent border-b border-white/10 rounded-none px-0 py-4 text-white placeholder:text-zinc-700 focus:border-[#ED2227] transition-colors"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('contact.subject')}</Label>
                    <Input
                      required
                      value={form.subject}
                      onChange={update('subject')}
                      className="bg-transparent border-b border-white/10 rounded-none px-0 py-4 text-white placeholder:text-zinc-700 focus:border-[#ED2227] transition-colors"
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('contact.message')}</Label>
                  <Textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update('message')}
                    className="bg-transparent border-b border-white/10 rounded-none px-0 py-4 text-white placeholder:text-zinc-700 focus:border-[#ED2227] resize-none transition-colors"
                    data-testid="contact-message-input"
                  />
                </div>

                {error && (
                  <p className="text-[#ED2227] text-sm" data-testid="contact-error">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={sending}
                  className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-12 h-12 text-sm gap-2"
                  data-testid="contact-submit-button"
                >
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  {sending ? t('contact.sending') : t('contact.send')}
                </Button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-5">
            <div className="space-y-8">
              <div>
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-6">
                  {t('contact.info')}
                </h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#ED2227] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div>
                      <span className="text-white text-sm block">{t('contact.address')}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-[#ED2227] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <a href="mailto:sales@partners4cars.com" className="text-white text-sm hover:text-[#ED2227] transition-colors duration-200">
                      {t('contact.emailAddress')}
                    </a>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 text-[#ED2227] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <a href="tel:+31625025292" className="text-white text-sm hover:text-[#ED2227] transition-colors duration-200">
                      {t('contact.phoneNumber')}
                    </a>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-[#121212] border border-zinc-800/50 rounded-md p-8">
                <FaWhatsapp className="w-8 h-8 text-[#25D366] mb-4" />
                <h3 className="text-lg font-bold text-white font-[Chivo] mb-2">
                  {t('contact.whatsappCta')}
                </h3>
                <p className="text-sm text-zinc-500 mb-6">
                  Get a response within minutes via WhatsApp.
                </p>
                <a
                  href="https://wa.me/31615254599?text=Hello%20Partners4Cars%2C%20I%20am%20interested%20in%20your%20vehicles."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="bg-[#25D366] text-white hover:bg-[#1da851] rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm gap-2"
                    data-testid="contact-whatsapp-button"
                  >
                    <FaWhatsapp className="w-5 h-5" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
